import {exhaustive} from '@freckle/exhaustive'
import type {NonEmptyArray} from '@freckle/non-empty'
import {flattenOnNonEmpty, mkNonEmptyFromHead} from '@freckle/non-empty'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'

export type MathAdaptiveT = 'math_adaptive'
export type MathTargetedT = 'math_targeted'
export type MathFocusSkillsPracticeT = 'focus_skills_practice'
export type MathAssessmentsT = 'math_assessments'
export type MathFactPracticeT = 'math_fact_practice'
export type MathIblsT = 'math_ibls'
export type MathNumberFactsT = 'math_number_facts'
export type MathNumberBasicsT = 'math_number_basics'
export type MathDepthOfKnowledgePracticeT = 'math_depth_of_knowledge_practice'
export type MathTargetedDepthOfKnowledgePracticeT = 'math_targeted_depth_of_knowledge_practice'
export type ElaDecodablesT = 'ela_decodables'
export type ElaTargetedSkillsPracticeT = 'ela_targeted_skills_practice'
export type ElaFocusSkillsPracticeT = 'ela_focus_skills_practice'
export type ElaAdaptiveSkillsPracticeT = 'ela_adaptive_skills_practice'
export type ElaWordStudyT = 'ela_word_study'
export type ElaSightWordsT = 'ela_sight_words'
export type ElaArticlesReadingT = 'ela_articles_reading'
export type ElaArticlesWritingT = 'ela_articles_writing'
export type ElaAssessmentsT = 'ela_assessments'
export type ScienceArticlesReadingT = 'science_articles_reading'
export type ScienceArticlesWritingT = 'science_articles_writing'
export type SocialStudiesArticlesReadingT = 'social_studies_articles_reading'
export type SocialStudiesArticlesWritingT = 'social_studies_articles_writing'

export type ProductT =
  | MathAdaptiveT
  | MathTargetedT
  | MathFocusSkillsPracticeT
  | MathAssessmentsT
  | MathFactPracticeT
  | MathIblsT
  | MathNumberFactsT
  | MathNumberBasicsT
  | MathDepthOfKnowledgePracticeT
  | MathTargetedDepthOfKnowledgePracticeT
  | ElaDecodablesT
  | ElaTargetedSkillsPracticeT
  | ElaFocusSkillsPracticeT
  | ElaAdaptiveSkillsPracticeT
  | ElaWordStudyT
  | ElaSightWordsT
  | ElaArticlesReadingT
  | ElaArticlesWritingT
  | ElaAssessmentsT
  | ScienceArticlesReadingT
  | ScienceArticlesWritingT
  | SocialStudiesArticlesReadingT
  | SocialStudiesArticlesWritingT

type MathProductPathT =
  | 'adaptive'
  | 'targeted'
  | 'focus-skills-practice'
  | 'assessments'
  | 'fact-practice'
  | 'ibls'
  | 'number-facts'
  | 'number-basics'
  | 'depth-of-knowledge-practice'
  | 'targeted-depth-of-knowledge-practice'
type ElaProductPathT =
  | 'assessments'
  | 'articles-reading'
  | 'articles-writing'
  | 'decodables'
  | 'targeted-skills-practice'
  | 'adaptive-skills-practice'
  | 'word-study'
  | 'sight-words'
  | 'ela-focus-skills-practice'
type ArticlePathT = 'articles-reading' | 'articles-writing'
type ProductPathT = MathProductPathT | ElaProductPathT | ArticlePathT

export type ProductPathsT = {
  subjectPath: 'math' | 'ela' | 'science' | 'social-studies'
  productPath: ProductPathT
}

export const Products = {
  MathAdaptive: 'math_adaptive' as MathAdaptiveT,
  MathTargeted: 'math_targeted' as MathTargetedT,
  MathFocusSkillsPractice: 'focus_skills_practice' as MathFocusSkillsPracticeT,
  MathAssessments: 'math_assessments' as MathAssessmentsT,
  MathFactPractice: 'math_fact_practice' as MathFactPracticeT,
  MathIbls: 'math_ibls' as MathIblsT,
  MathNumberFacts: 'math_number_facts' as MathNumberFactsT,
  MathNumberBasics: 'math_number_basics' as MathNumberBasicsT,
  MathDepthOfKnowledgePractice: 'math_depth_of_knowledge_practice' as MathDepthOfKnowledgePracticeT,
  MathTargetedDepthOfKnowledgePractice:
    'math_targeted_depth_of_knowledge_practice' as MathTargetedDepthOfKnowledgePracticeT,
  ElaDecodables: 'ela_decodables' as ElaDecodablesT,
  ElaTargetedSkillsPractice: 'ela_targeted_skills_practice' as ElaTargetedSkillsPracticeT,
  ElaFocusSkillsPractice: 'ela_focus_skills_practice' as ElaFocusSkillsPracticeT,
  ElaAdaptiveSkillsPractice: 'ela_adaptive_skills_practice' as ElaAdaptiveSkillsPracticeT,
  ElaWordStudy: 'ela_word_study' as ElaWordStudyT,
  ElaSightWords: 'ela_sight_words' as ElaSightWordsT,
  ElaArticlesReading: 'ela_articles_reading' as ElaArticlesReadingT,
  ElaArticlesWriting: 'ela_articles_writing' as ElaArticlesWritingT,
  ElaAssessments: 'ela_assessments' as ElaAssessmentsT,
  ScienceArticlesReading: 'science_articles_reading' as ScienceArticlesReadingT,
  ScienceArticlesWriting: 'science_articles_writing' as ScienceArticlesWritingT,
  SocialStudiesArticlesReading: 'social_studies_articles_reading' as SocialStudiesArticlesReadingT,
  SocialStudiesArticlesWriting: 'social_studies_articles_writing' as SocialStudiesArticlesWritingT,

  parse: (text: string): ProductT | undefined => Products.all().find(value => value === text),

  getMathProducts: (): NonEmptyArray<ProductT> => [
    Products.MathAdaptive,
    Products.MathTargeted,
    Products.MathFocusSkillsPractice,
    Products.MathAssessments,
    Products.MathFactPractice,
    Products.MathIbls,
    Products.MathNumberFacts,
    Products.MathNumberBasics,
    Products.MathDepthOfKnowledgePractice,
    Products.MathTargetedDepthOfKnowledgePractice,
  ],

  getElaProducts: (): NonEmptyArray<ProductT> =>
    mkNonEmptyFromHead(Products.ElaDecodables, [
      Products.ElaTargetedSkillsPractice,
      Products.ElaFocusSkillsPractice,
      Products.ElaAdaptiveSkillsPractice,
      Products.ElaWordStudy,
      Products.ElaSightWords,
      Products.ElaArticlesReading,
      Products.ElaArticlesWriting,
      Products.ElaAssessments,
    ]),

  getScienceProducts: (): NonEmptyArray<ProductT> =>
    mkNonEmptyFromHead(Products.ScienceArticlesReading, [Products.ScienceArticlesWriting]),

  getSocialStudiesProducts: (): NonEmptyArray<ProductT> =>
    mkNonEmptyFromHead(Products.SocialStudiesArticlesReading, [
      Products.SocialStudiesArticlesWriting,
    ]),

  all(): NonEmptyArray<ProductT> {
    return flattenOnNonEmpty(
      mkNonEmptyFromHead(this.getMathProducts(), [
        this.getElaProducts(),
        this.getScienceProducts(),
        this.getSocialStudiesProducts(),
      ]),
    )
  },

  getProducts(subject: SubjectT): NonEmptyArray<ProductT> {
    switch (subject) {
      case 'math':
        return this.getMathProducts()

      case 'ela':
        return this.getElaProducts()

      case 'science':
        return this.getScienceProducts()

      case 'social_studies':
        return this.getSocialStudiesProducts()

      default:
        return exhaustive(subject, 'SubjectT')
    }
  },

  products(contentArea: ProductPathsT['subjectPath']): NonEmptyArray<ProductT> {
    switch (contentArea) {
      case 'math':
        return this.getMathProducts()
      case 'ela':
        return this.getElaProducts()
      case 'science':
        return this.getScienceProducts()
      case 'social-studies':
        return this.getSocialStudiesProducts()
      default:
        return exhaustive(contentArea)
    }
  },

  getSubject(productPath: ProductPathsT): SubjectT {
    switch (productPath.subjectPath) {
      case 'social-studies':
        return 'social_studies'
      default:
        return productPath.subjectPath
    }
  },

  getPaths(product: ProductT): ProductPathsT {
    const mathSubjectPath = 'math'
    const elaSubjectPath = 'ela'
    const scienceSubjectPath = 'science'
    const socialStudiesSubjectPath = 'social-studies'

    switch (product) {
      case 'math_adaptive':
        return {subjectPath: mathSubjectPath, productPath: 'adaptive'}
      case 'math_targeted':
        return {subjectPath: mathSubjectPath, productPath: 'targeted'}
      case 'focus_skills_practice':
        return {subjectPath: mathSubjectPath, productPath: 'focus-skills-practice'}
      case 'math_assessments':
        return {subjectPath: mathSubjectPath, productPath: 'assessments'}
      case 'math_fact_practice':
        return {subjectPath: mathSubjectPath, productPath: 'fact-practice'}
      case 'math_ibls':
        return {subjectPath: mathSubjectPath, productPath: 'ibls'}
      case 'math_number_facts':
        return {subjectPath: mathSubjectPath, productPath: 'number-facts'}
      case 'math_number_basics':
        return {subjectPath: mathSubjectPath, productPath: 'number-basics'}
      case 'math_depth_of_knowledge_practice':
        return {subjectPath: mathSubjectPath, productPath: 'depth-of-knowledge-practice'}
      case 'math_targeted_depth_of_knowledge_practice':
        return {subjectPath: mathSubjectPath, productPath: 'targeted-depth-of-knowledge-practice'}

      case 'ela_decodables':
        return {subjectPath: elaSubjectPath, productPath: 'decodables'}
      case 'ela_targeted_skills_practice':
        return {subjectPath: elaSubjectPath, productPath: 'targeted-skills-practice'}
      case 'ela_focus_skills_practice':
        return {subjectPath: elaSubjectPath, productPath: 'focus-skills-practice'}
      case 'ela_adaptive_skills_practice':
        return {subjectPath: elaSubjectPath, productPath: 'adaptive-skills-practice'}
      case 'ela_word_study':
        return {subjectPath: elaSubjectPath, productPath: 'word-study'}
      case 'ela_sight_words':
        return {subjectPath: elaSubjectPath, productPath: 'sight-words'}
      case 'ela_articles_reading':
        return {subjectPath: elaSubjectPath, productPath: 'articles-reading'}
      case 'ela_articles_writing':
        return {subjectPath: elaSubjectPath, productPath: 'articles-writing'}
      case 'ela_assessments':
        return {subjectPath: elaSubjectPath, productPath: 'assessments'}

      case 'science_articles_reading':
        return {subjectPath: scienceSubjectPath, productPath: 'articles-reading'}
      case 'science_articles_writing':
        return {subjectPath: scienceSubjectPath, productPath: 'articles-writing'}

      case 'social_studies_articles_reading':
        return {subjectPath: socialStudiesSubjectPath, productPath: 'articles-reading'}
      case 'social_studies_articles_writing':
        return {subjectPath: socialStudiesSubjectPath, productPath: 'articles-writing'}

      default:
        return exhaustive(product, 'ProductT')
    }
  },

  getPracticeMode: function (t: TranslateT, product: ProductT): string {
    return t('common|PRACTICE_MODE_FROM_PRODUCT', {product})
  },
}
